import { useEffect, useMemo, useState } from 'react';
import { useHeader } from '@/contexts';
import { useListaContribuintes } from '@/services/Contribuintes';
import {
  buildEmpresaDropdownItems,
  matchesEmpresaSearch,
  sortContribuintesForDropdown,
} from '@/lib/utils/contribuintes';

export function useServicosNfseHeader() {
  const { filtros, setEmpresa, setEmpresaCnpjs, setDataInicio, setDataFim } = useHeader();
  const [cnpjSelecionado, setCnpjSelecionado] = useState(filtros.empresaId || '');
  const [searchEmpresa, setSearchEmpresa] = useState('');
  const [modoGruposEmpresa, setModoGruposEmpresa] = useState(false);
  const [gruposEmpresaAbertos, setGruposEmpresaAbertos] = useState<Record<string, boolean>>({});
  const [empresaSelectOpen, setEmpresaSelectOpen] = useState(false);
  const { data: contribuintes, isLoading: isLoadingContribuintes } = useListaContribuintes();

  useEffect(() => {
    setCnpjSelecionado(filtros.empresaId || '');
  }, [filtros.empresaId]);

  const contribuintesOrdenados = useMemo(() => {
    return sortContribuintesForDropdown(contribuintes);
  }, [contribuintes]);

  const contribuintesFiltrados = useMemo(() => {
    return contribuintesOrdenados.filter(contribuinte =>
      matchesEmpresaSearch(contribuinte, searchEmpresa)
    );
  }, [contribuintesOrdenados, searchEmpresa]);

  const dropdownEmpresasAgrupadas = useMemo(() => {
    return buildEmpresaDropdownItems(contribuintesOrdenados);
  }, [contribuintesOrdenados]);

  const dropdownEmpresasAgrupadasFiltradas = useMemo(() => {
    if (!searchEmpresa) return dropdownEmpresasAgrupadas;
    return dropdownEmpresasAgrupadas.filter(item => {
      if (item.kind === 'single') return matchesEmpresaSearch(item.empresa, searchEmpresa);
      return (
        matchesEmpresaSearch(item.matriz, searchEmpresa) ||
        item.vinculadas.some(v => matchesEmpresaSearch(v, searchEmpresa))
      );
    });
  }, [dropdownEmpresasAgrupadas, searchEmpresa]);

  const handleEmpresaChange = (value: string) => {
    if (value === cnpjSelecionado) {
      setCnpjSelecionado('');
      setEmpresa(null);
      setEmpresaCnpjs(null);
      return;
    }

    setCnpjSelecionado(value);
    setEmpresa(value);
    setEmpresaCnpjs(value);
  };

  const handleGrupoChange = (matrizCnpj: string, cnpjsCsv: string) => {
    if (matrizCnpj === cnpjSelecionado) {
      setCnpjSelecionado('');
      setEmpresa(null);
      setEmpresaCnpjs(null);
      return;
    }

    setCnpjSelecionado(matrizCnpj);
    setEmpresa(matrizCnpj);
    setEmpresaCnpjs(cnpjsCsv);
  };

  const handleToggleModoGruposEmpresa = () => {
    setModoGruposEmpresa(prev => !prev);
    setCnpjSelecionado('');
    setEmpresa(null);
    setEmpresaCnpjs(null);
  };

  return {
    cnpjSelecionado,
    contribuintes,
    contribuintesFiltrados,
    dropdownEmpresasAgrupadasFiltradas,
    empresaSelectOpen,
    filtros,
    gruposEmpresaAbertos,
    handleEmpresaChange,
    handleGrupoChange,
    handleToggleModoGruposEmpresa,
    isLoadingContribuintes,
    modoGruposEmpresa,
    searchEmpresa,
    setDataFim,
    setDataInicio,
    setEmpresaSelectOpen,
    setGruposEmpresaAbertos,
    setSearchEmpresa,
  };
}
